import React, { useState, useRef } from 'react';
import { supabase } from './lib/supabase';

const C = {
  bg:'#f4f6f9', white:'#ffffff', ink:'#0a0a0a',
  blue:'#0071eb', blueL:'#e8f2ff', blueB:'rgba(0,113,235,0.2)',
  grey:'#6b7280', grey2:'#9ca3af', grey4:'#f3f4f6',
  green:'#10b981', red:'#ef4444', gold:'#f59e0b',
  border:'#e5e7eb', med:'#374151',
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export default function UploadScreen({ user, onBack, onHome }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isLocal, setIsLocal] = useState(false);
  const fileRef = useRef();

  const categories = ['Sports','Talk Show','Podcast','SA Original','Drama','Comedy','Reality','Lifestyle','Music','Business','Health','Education'];

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setStep(2); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('video/')) { setFile(f); setStep(2); }
  };

  const handleUpload = async () => {
    if (!title || !category) { setError('Please add a title and select a category.'); return; }
    setError('');
    setUploading(true);
    setStep(3);

    try {
      // Step 1: Get Mux upload URL from Edge Function
      const fnRes = await fetch(SUPABASE_URL + '/functions/v1/mux-upload', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });
      const fnData = await fnRes.json();
      if (!fnData.upload_url) throw new Error('Could not get upload URL from Mux.');

      const uploadUrl = fnData.upload_url;
      const uploadId = fnData.upload_id;

      // Step 2: Upload video directly to Mux using XMLHttpRequest for progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error('Upload failed with status ' + xhr.status));
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload.')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Step 3: Save show record to Supabase
      const { error: dbError } = await supabase
        .from('shows')
        .insert({
          title: title,
          description: description,
          category: category,
          type: file.size < 100000000 ? 'short' : 'series',
          creator_id: user.id,
          is_local: isLocal,
          is_new: true,
          episode_count: 1,
          mux_upload_id: uploadId,
        });

      if (dbError) console.log('DB warning:', dbError.message);

      setStep(4);

    } catch (e) {
      setError(e.message || 'Upload failed. Please try again.');
      setStep(2);
    }
    setUploading(false);
  };

  const inp = {
    width:'100%', padding:'12px 16px',
    border:'1.5px solid '+C.border, borderRadius:8,
    fontSize:14, outline:'none', fontFamily:'inherit',
    marginBottom:14, boxSizing:'border-box', background:C.white,
  };

  const steps = ['Select Video','Add Details','Uploading','Complete'];

  return (
    <div style={{background:'linear-gradient(180deg,#071523 0%,#0b1d31 100%)', minHeight:'100vh', paddingTop:64}}>
      <div style={{background:'rgba(255,255,255,0.04)', borderBottom:'1px solid rgba(161,204,255,0.12)', padding:'20px 40px', display:'flex', alignItems:'center', gap:16, backdropFilter:'blur(14px)'}}>
        <button onClick={onBack} style={{padding:'8px 14px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(161,204,255,0.12)',borderRadius:999,color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>Studio</button>
        <button onClick={onHome} style={{padding:'8px 14px',background:'linear-gradient(135deg,rgba(0,130,255,0.24),rgba(0,88,185,0.16))',border:'1px solid rgba(122,196,255,0.28)',borderRadius:999,color:'#fff',cursor:'pointer',fontSize:12,fontWeight:800,fontFamily:'inherit'}}>Home</button>
        <div style={{fontSize:24,fontWeight:800,color:'#fff'}}>Upload Video</div>
      </div>

      <div style={{background:'rgba(255,255,255,0.03)', borderBottom:'1px solid rgba(161,204,255,0.12)', padding:'16px 40px', display:'flex', alignItems:'center', backdropFilter:'blur(14px)'}}>
        {steps.map((s,i) => (
          <div key={i} style={{display:'flex',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'0 16px'}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:step>i+1?C.green:step===i+1?C.blue:'rgba(255,255,255,0.08)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,flexShrink:0}}>
                {step>i+1 ? '✓' : i+1}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:step===i+1?C.blue:step>i+1?C.green:'#bfd0df'}}>{s}</div>
            </div>
            {i<3 && <div style={{width:32,height:1,background:'rgba(161,204,255,0.12)'}}/>}
          </div>
        ))}
      </div>

      <div style={{maxWidth:720,margin:'0 auto',padding:'32px 40px 100px'}}>

        {step===1 && (
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.ink,marginBottom:6}}>Select your video file</div>
            <div style={{fontSize:13,color:C.grey,marginBottom:24}}>MP4, MOV, or AVI · Up to 4GB</div>
            <div
              onDragOver={e=>e.preventDefault()}
              onDrop={handleDrop}
              onClick={()=>fileRef.current.click()}
              style={{border:'2px dashed rgba(161,204,255,0.12)',borderRadius:20,padding:'64px 32px',textAlign:'center',cursor:'pointer',background:'rgba(255,255,255,0.04)',transition:'all 0.2s',color:'#fff'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.background='rgba(0,130,255,0.08)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(161,204,255,0.12)';e.currentTarget.style.background='rgba(255,255,255,0.04)';}}>
              <div style={{fontSize:56,marginBottom:16}}>🎬</div>
              <div style={{fontSize:18,fontWeight:700,color:'#fff',marginBottom:8}}>Drop your video here</div>
              <div style={{fontSize:13,color:'#bfd0df',marginBottom:20}}>or click to browse your files</div>
              <button style={{padding:'10px 24px',background:C.blue,color:C.white,border:'none',borderRadius:999,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Browse Files</button>
              <input ref={fileRef} type="file" accept="video/*" style={{display:'none'}} onChange={handleFileSelect}/>
            </div>
          </div>
        )}

        {step===2 && (
          <div>
            <div style={{fontSize:18,fontWeight:800,color:C.ink,marginBottom:6}}>Add video details</div>
            <div style={{fontSize:13,color:C.grey,marginBottom:24}}>
              {file && `Selected: ${file.name} · ${(file.size/1024/1024).toFixed(1)}MB`}
            </div>
            {error && <div style={{background:'#fee2e2',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,padding:'12px 16px',fontSize:13,color:C.red,marginBottom:16}}>{error}</div>}
            <div style={{background:C.white,borderRadius:12,padding:24,border:'1px solid '+C.border,marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:700,marginBottom:16}}>Video Information</div>
              <label style={{display:'block',fontSize:12,fontWeight:600,color:C.med,marginBottom:6}}>Title *</label>
              <input style={inp} placeholder="Give your video a compelling title..." value={title} onChange={e=>setTitle(e.target.value)} maxLength={80}/>
              <label style={{display:'block',fontSize:12,fontWeight:600,color:C.med,marginBottom:6}}>Description</label>
              <textarea style={{...inp,minHeight:100,resize:'vertical',lineHeight:1.6}} placeholder="What is this video about?" value={description} onChange={e=>setDescription(e.target.value)}/>
              <label style={{display:'block',fontSize:12,fontWeight:600,color:C.med,marginBottom:8}}>Content Type *</label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:16}}>
                {categories.map(c=>(
                  <button key={c} onClick={()=>setCategory(c)}
                    style={{padding:'10px 8px',border:'1.5px solid '+(category===c?C.blue:C.border),borderRadius:8,background:category===c?C.blueL:C.white,color:category===c?C.blue:C.grey,cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,transition:'all 0.18s'}}>
                    {c}
                  </button>
                ))}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:C.bg,borderRadius:8,cursor:'pointer'}} onClick={()=>setIsLocal(!isLocal)}>
                <div style={{width:20,height:20,borderRadius:4,border:'2px solid '+(isLocal?C.blue:C.border),background:isLocal?C.blue:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.18s'}}>
                  {isLocal && <span style={{color:C.white,fontSize:12,fontWeight:800}}>✓</span>}
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.ink}}>🇿🇦 This is SA Original content</div>
                  <div style={{fontSize:11,color:C.grey}}>Mark this if the content was created in South Africa</div>
                </div>
              </div>
            </div>
            <div style={{background:C.blueL,border:'1px solid '+C.blueB,borderRadius:10,padding:'14px 18px',marginBottom:24,display:'flex',gap:12}}>
              <div style={{fontSize:18,flexShrink:0}}>💡</div>
              <div style={{fontSize:12,color:C.med,lineHeight:1.6}}>As a Founding Creator you earn 75% of pool revenue for your first 12 months. Your video is processed by Mux and available to stream immediately after upload.</div>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setStep(1)} style={{padding:'12px 24px',border:'1px solid '+C.border,borderRadius:8,background:C.white,color:C.grey,cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:600}}>Back</button>
              <button onClick={handleUpload} style={{flex:1,padding:'12px 24px',background:C.blue,color:C.white,border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Upload to VAULTT →</button>
            </div>
          </div>
        )}

        {step===3 && (
          <div style={{textAlign:'center',padding:'48px 0'}}>
            <div style={{fontSize:56,marginBottom:24}}>⏳</div>
            <div style={{fontSize:20,fontWeight:800,color:C.ink,marginBottom:8}}>Uploading to Mux...</div>
            <div style={{fontSize:13,color:C.grey,marginBottom:32}}>Do not close this page. Your video is being uploaded and processed.</div>
            <div style={{background:C.white,borderRadius:12,padding:24,border:'1px solid '+C.border,marginBottom:24}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:13,fontWeight:600,color:C.ink}}>{title}</span>
                <span style={{fontSize:13,color:C.blue,fontWeight:700}}>{uploadProgress}%</span>
              </div>
              <div style={{height:8,background:C.grey4,borderRadius:4,overflow:'hidden'}}>
                <div style={{height:'100%',background:C.blue,width:uploadProgress+'%',borderRadius:4,transition:'width 0.3s'}}/>
              </div>
              <div style={{fontSize:12,color:C.grey,marginTop:8}}>
                {uploadProgress < 30 ? 'Uploading...' : uploadProgress < 70 ? 'Processing...' : uploadProgress < 100 ? 'Almost done...' : 'Finalising...'}
              </div>
            </div>
            {file && <div style={{fontSize:12,color:C.grey}}>{file.name} · {(file.size/1024/1024).toFixed(1)}MB</div>}
          </div>
        )}

        {step===4 && (
          <div style={{textAlign:'center',padding:'48px 0'}}>
            <div style={{width:80,height:80,borderRadius:'50%',background:'#d1fae5',border:'3px solid '+C.green,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,margin:'0 auto 24px'}}>✓</div>
            <div style={{fontSize:28,fontWeight:900,color:C.ink,marginBottom:8}}>Video Published!</div>
            <div style={{fontSize:14,color:C.grey,marginBottom:32,lineHeight:1.65}}>Your video has been uploaded to Mux and is now processing. It will be available to stream on VAULTT within a few minutes. You will earn from every viewer immediately.</div>
            <div style={{background:C.white,borderRadius:12,padding:24,border:'1px solid '+C.border,marginBottom:24,textAlign:'left'}}>
              <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:8}}>{title}</div>
              <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                <div style={{fontSize:12,color:C.grey}}>Category: <strong style={{color:C.ink}}>{category}</strong></div>
                {isLocal && <div style={{fontSize:12,color:C.blue,fontWeight:600}}>🇿🇦 SA Original</div>}
                <div style={{fontSize:12,color:C.green,fontWeight:600}}>✓ In Revenue Pool</div>
                <div style={{fontSize:12,color:C.green,fontWeight:600}}>✓ Processing on Mux</div>
              </div>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>{setStep(1);setFile(null);setTitle('');setDescription('');setCategory('');setIsLocal(false);setUploadProgress(0);}} style={{padding:'12px 24px',border:'1px solid '+C.border,borderRadius:8,background:C.white,color:C.grey,cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:600}}>Upload Another</button>
              <button onClick={onBack} style={{padding:'12px 24px',background:C.blue,color:C.white,border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Back to Studio</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
